import { API } from 'aws-amplify';
import { Box, TextInput, Text } from 'grommet';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import Modal from './Modal';
import { useUsers } from '../../context/users';
import { createUnit, updateUnit, updateTenant } from '../../graphql/mutations';
import StyledButton from '../StyledButton';

// TODO: probably want data validation so it's not too big
// prolly on server side

interface ModalProps {
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  unitID: number;
}

const UnitModal = ({ setShow, unitID }: ModalProps): JSX.Element => {
  const { units, users, boxes, facilityInfo } = useUsers();

  // unitID 0 (the value "+ Add unit" passes) means create mode.
  const isNew = !unitID;
  const existing = units.find(row => row.id === unitID);

  const tenant = users.find(user => user.unitID === String(unitID));
  const tenantBoxesInStorage = boxes.filter(
    row => row.tenantID === tenant?.id && row.status === 'IN_STORAGE',
  );

  // Form state — initialized from the existing unit in edit mode, blank for create.
  const [unitLabel, setUnitLabel] = React.useState(existing?.unitID ?? '');
  const [sizeValue, setSize] = React.useState(existing?.size ?? '');
  const [priceValue, setPrice] = React.useState(existing?.price ?? '');
  const [usageValue, setUsage] = React.useState(existing?.usage ?? '0%');
  const [tenantValue] = React.useState(existing?.tenantID ?? ' ');
  const [saving, setSaving] = React.useState(false);

  const createNewUnit = async () => {
    if (saving) return;
    if (!facilityInfo) { alert('No facility loaded — cannot create unit.'); return; }
    if (!unitLabel.trim() || !sizeValue.trim()) {
      alert('Unit label and size are required.');
      return;
    }
    setSaving(true);
    try {
      await API.graphql({
        query: createUnit,
        variables: {
          input: {
            id: uuidv4(),
            facilityID: facilityInfo.id,
            unitID: unitLabel.trim(),
            size: sizeValue.trim(),
            price: priceValue.trim() || null,
            usage: '0%',
            availability: 'VACANT',
            tenantID: ' ',
          },
        },
      });
      setShow(false);
      window.location.reload();
    } catch (e) {
      console.error('Error creating unit:', e);
      alert('Failed to create unit. Please try again.');
      setSaving(false);
    }
  };

  const saveUnit = async () => {
    if (saving || !existing) return;
    setSaving(true);
    try {
      const unit = { id: existing.id, tenantID: tenantValue, usage: usageValue, price: priceValue, size: sizeValue };
      await API.graphql({ query: updateUnit, variables: { input: unit } });
      setShow(false);
      window.location.reload();
    } catch (e) {
      console.error('Error saving unit:', e);
      alert('Failed to save unit. Please try again.');
      setSaving(false);
    }
  };

  const moveOut = async () => {
    if (tenantBoxesInStorage && tenantBoxesInStorage.length > 0) {
      alert('Tenant still has items in storage!');
      return;
    }
    if (!window.confirm('Are you sure you want to proceed?')) return;

    const tenantDetail = { id: tenantValue, unitID: null };
    const unitDetail = {
      id: unitID,
      availability: 'VACANT',
      tenantID: ' ',
      usage: '0%',
    };

    try {
      await API.graphql({ query: updateTenant, variables: { input: tenantDetail } });
      await API.graphql({ query: updateUnit, variables: { input: unitDetail } });
      window.location.reload();
    } catch (e: unknown) {
      console.log(e);
    }
  };

  // ── Create mode ────────────────────────────────────────────────────────────
  if (isNew) {
    return (
      <Modal setShow={setShow} header="Add Unit">
        <Box gap="medium" pad={{ horizontal: 'large' }}>
          <Box gap="small">
            <Text weight="bold">Unit label</Text>
            <TextInput
              placeholder="e.g. A101"
              value={unitLabel}
              onChange={event => setUnitLabel(event.target.value)}
            />
            <Text weight="bold">Size</Text>
            <TextInput
              placeholder="e.g. 5x5"
              value={sizeValue}
              onChange={event => setSize(event.target.value)}
            />
            <Text weight="bold">Monthly price</Text>
            <TextInput
              placeholder="e.g. 30"
              value={priceValue}
              onChange={event => setPrice(event.target.value)}
            />
          </Box>
          <Box direction="row" justify="end">
            <StyledButton
              label={saving ? 'Saving…' : 'Create Unit'}
              onClick={createNewUnit}
            />
          </Box>
        </Box>
      </Modal>
    );
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  if (!existing) {
    return (
      <Modal setShow={setShow} header="Edit Unit">
        <Box pad={{ horizontal: 'large' }}>
          <Text>Unit not found.</Text>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal setShow={setShow} header="Edit Unit">
      <Box gap="medium" pad={{ horizontal: 'large' }}>
        <Box direction="column" gap="medium">
          <Box height="medium" gap="small">
            <Box direction="row" gap="medium" margin={{ bottom: 'medium' }}>
              <Text weight="bold">
                Unit ID:{' '}
                <Text weight="normal" color="dark-3">
                  {existing.id}
                </Text>
              </Text>
              <Text weight="bold">
                Tenant:{' '}
                <Text weight="normal" color="dark-3">
                  {existing.tenantID}
                </Text>
              </Text>
            </Box>
            <Text weight="bold">
              Size:{' '}
              <TextInput
                value={sizeValue}
                onChange={event => setSize(event.target.value)}
              />
            </Text>
            <Text weight="bold">
              Price:{' '}
              <TextInput
                value={priceValue}
                onChange={event => setPrice(event.target.value)}
              />
            </Text>
            <Text weight="bold">
              Usage:{' '}
              <TextInput
                value={usageValue}
                onChange={event => setUsage(event.target.value)}
              />
            </Text>
          </Box>
        </Box>
        <Box direction="row" justify="between">
          <StyledButton label="Move Out" onClick={() => moveOut()} />
          <StyledButton label={saving ? 'Saving…' : 'Save'} onClick={saveUnit} />
        </Box>
      </Box>
    </Modal>
  );
};

export default UnitModal;
